/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file chatShapes.js
 * @description
 * Pure, dependency-free shape guards + helpers of the Team Chat module. Every
 * backend object is treated as untrusted: `sender` may be null, a user `name`
 * may be the nested `{first, last}` object (rendering it raw = React #31), ids
 * may be ObjectId-ish objects, arrays may be missing. Screens only ever render
 * values that went through these helpers.
 */

/** Messages closer than this from the same sender render as one group. */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

/** Emojis offered in the quick-reaction row and the composer strip. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '🙏', '✅', '👀'];

/** `@channel` mention tokens (EN + HU spelling). */
const CHANNEL_MENTION_RE = /(^|\s)@(channel|here|mindenki|csatorna)\b/i;

/**
 * Safe array coercion.
 * @param {*} value
 * @returns {Array}
 */
export const arr = (value) => (Array.isArray(value) ? value : []);

/**
 * Stringify an id (string, ObjectId-like, populated doc).
 * @param {*} value
 * @returns {string} '' when absent.
 */
export const idOf = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'object') {
        const inner = value._id || value.id || value.$oid;
        if (inner && inner !== value) {
            return idOf(inner);
        }
        if (typeof value.toString === 'function') {
            const text = value.toString();
            return text && text !== '[object Object]' ? text : '';
        }
    }

    return '';
};

/**
 * Human name of a person-ish value: string, `{name: {first,last}}`,
 * `{name: 'x'}`, `{firstName,lastName}`, `{email}`. Never returns an object.
 * @param {*} person
 * @param {string} [fallback='']
 * @returns {string}
 */
export const personName = (person, fallback = '') => {
    if (!person) {
        return fallback;
    }
    if (typeof person === 'string') {
        return person.trim() || fallback;
    }
    if (typeof person !== 'object') {
        return String(person);
    }
    const name = person.name;
    if (name && typeof name === 'object') {
        const joined = [name.first, name.last].filter((part) => typeof part === 'string' && part.trim()).join(' ').trim();
        if (joined) {
            return joined;
        }
    }
    if (typeof name === 'string' && name.trim()) {
        return name.trim();
    }
    const composed = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    if (composed) {
        return composed;
    }
    const other = person.displayName || person.username || person.email;

    return typeof other === 'string' && other.trim() ? other.trim() : fallback;
};

/**
 * Plain text from any value (guards against objects leaking into <Text>).
 * @param {*} value
 * @returns {string}
 */
export const textOf = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (typeof value === 'object') {
        return personName(value, '') || textOf(value.label || value.text || value.title || '');
    }

    return '';
};

/**
 * Time value in ms (0 when unparsable).
 * @param {*} value
 * @returns {number}
 */
export const timeOf = (value) => {
    if (!value) {
        return 0;
    }
    const ms = new Date(value).getTime();

    return Number.isFinite(ms) ? ms : 0;
};

/**
 * Random-enough client id used for optimistic send + idempotent retries.
 * @returns {string}
 */
export const makeClientMessageId = () => {
    const rand = Math.random().toString(36).slice(2, 10);

    return `m-${Date.now().toString(36)}-${rand}`;
};

/**
 * Normalize a channel object.
 * @param {*} raw
 * @returns {Object|null}
 */
export const normalizeChannel = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const id = idOf(raw._id || raw.id);
    if (!id) {
        return null;
    }
    const me = raw.me && typeof raw.me === 'object' ? raw.me : null;

    return {
        ...raw,
        _id: id,
        type: raw.type || 'public',
        name: textOf(raw.name),
        description: textOf(raw.description),
        topic: textOf(raw.topic),
        archived: !!raw.archived,
        directPeers: arr(raw.directPeers).filter(Boolean),
        memberCount: Number(raw.memberCount) || 0,
        pinnedCount: Number(raw.pinnedCount) || 0,
        lastMessagePreview: raw.lastMessagePreview && typeof raw.lastMessagePreview === 'object' ? raw.lastMessagePreview : null,
        me: me ? {
            ...me,
            role: me.role || 'member',
            unreadCount: Number(me.unreadCount) || 0,
            mentionCount: Number(me.mentionCount) || 0,
            notifyLevel: me.notifyLevel || 'all',
            starred: !!me.starred,
            hidden: !!me.hidden,
        } : null,
    };
};

/**
 * Normalize a message object (arrays guaranteed, ids stringified).
 * @param {*} raw
 * @returns {Object|null}
 */
export const normalizeMessage = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    return {
        ...raw,
        _id: idOf(raw._id || raw.id) || undefined,
        channel: idOf(raw.channel),
        sender: raw.sender && typeof raw.sender === 'object' ? raw.sender : null,
        kind: raw.kind || 'text',
        text: typeof raw.text === 'string' ? raw.text : textOf(raw.text),
        attachments: arr(raw.attachments).filter((att) => att && typeof att === 'object'),
        reactions: arr(raw.reactions).filter((r) => r && r.emoji),
        recordRefs: arr(raw.recordRefs).filter((r) => r && r.kind && r.id),
        replyUsers: arr(raw.replyUsers).filter(Boolean),
        replyCount: Number(raw.replyCount) || 0,
        threadRoot: idOf(raw.threadRoot) || null,
        mentions: raw.mentions && typeof raw.mentions === 'object' ? raw.mentions : {users: [], channel: false},
        meeting: raw.meeting && typeof raw.meeting === 'object' ? raw.meeting : null,
    };
};

/**
 * Merge incoming messages into a list (dedupe by `_id`, then by
 * `clientMessageId` so the server echo replaces the optimistic bubble).
 * Result sorted oldest → newest; pending (unsent) items stay last.
 *
 * @param {Array} current Existing list.
 * @param {Array} incoming New/updated messages.
 * @returns {Array}
 */
export const mergeMessages = (current, incoming) => {
    const list = arr(current).slice();
    arr(incoming).forEach((rawItem) => {
        const item = normalizeMessage(rawItem);
        if (!item) {
            return;
        }
        let index = item._id ? list.findIndex((m) => m._id && m._id === item._id) : -1;
        if (index < 0 && item.clientMessageId) {
            index = list.findIndex((m) => m.clientMessageId && m.clientMessageId === item.clientMessageId);
        }
        if (index >= 0) {
            const previous = list[index];
            list[index] = {...previous, ...item, localStatus: item.localStatus || undefined};
        } else {
            list.push(item);
        }
    });

    return list.sort((a, b) => {
        const aLocal = a.localStatus ? 1 : 0;
        const bLocal = b.localStatus ? 1 : 0;
        if (aLocal !== bLocal) {
            return aLocal - bLocal;
        }

        return timeOf(a.createdAt) - timeOf(b.createdAt);
    });
};

/**
 * Whether a channel is the private discussion under a shared-mailbox mail thread
 * (`type:'record'`, `record.kind === 'mail_thread'`, `mailThread: {address, conversationKey}`).
 * @param {Object} channel
 * @returns {boolean}
 */
export const isMailThreadChannel = (channel) => Boolean(channel)
    && channel.type === 'record'
    && (channel.record?.kind === 'mail_thread' || Boolean(channel.mailThread?.conversationKey));

/**
 * Mailbox address + conversation key of a mail-thread discussion channel.
 * @param {Object} channel
 * @returns {{address: string, conversationKey: string}|null} null for any other channel.
 */
export const mailThreadOf = (channel) => {
    if (!isMailThreadChannel(channel)) {
        return null;
    }
    const info = channel.mailThread && typeof channel.mailThread === 'object' ? channel.mailThread : {};

    return {
        address: String(info.address || '').trim().toLowerCase(),
        conversationKey: String(info.conversationKey || '').trim().toLowerCase(),
    };
};

/**
 * Display title of a channel for the given viewer.
 * @param {Object} channel
 * @param {string} meId
 * @returns {string}
 */
export const channelTitle = (channel, meId) => {
    if (!channel) {
        return '';
    }
    if (channel.type === 'direct' || channel.type === 'group') {
        const peers = arr(channel.directPeers).filter((peer) => idOf(peer) !== String(meId || ''));
        const names = peers.map((peer) => personName(peer)).filter(Boolean);
        if (names.length) {
            return names.join(', ');
        }
    }
    if (isMailThreadChannel(channel)) {
        // Mail discussion: the channel name IS the mail subject.
        return textOf(channel.name) || textOf(channel.record?.label);
    }
    if (channel.type === 'record' && channel.record) {
        return textOf(channel.record.label) || textOf(channel.name);
    }
    if (channel.type === 'customer' && channel.customer) {
        return textOf(channel.customer.name) || textOf(channel.name);
    }

    return textOf(channel.name);
};

/**
 * Predefined glyphs a public/private channel may pick (mirror of the backend `CHANNEL_ICONS`;
 * the server rejects anything else with 400 INVALID_CHANNEL_ICON).
 */
export const CHANNEL_ICON_CHOICES = [
    'pound', 'bullhorn-outline', 'chat-outline', 'forum-outline', 'lightbulb-on-outline', 'star-outline',
    'heart-outline', 'rocket-launch-outline', 'cog-outline', 'wrench-outline', 'cash-multiple', 'chart-line',
    'cart-outline', 'truck-delivery-outline', 'food-outline', 'silverware-fork-knife', 'coffee-outline',
    'shield-check-outline', 'calendar-month-outline', 'clipboard-text-outline', 'headset', 'school-outline',
    'party-popper', 'bug-outline', 'palette-outline', 'cellphone', 'newspaper-variant-outline', 'handshake-outline',
];

/**
 * Store (partner) channel — a customer channel shared with another store; shows that store's logo.
 * @param {Object} channel
 * @returns {boolean}
 */
export const isStoreChannel = (channel) => channel?.type === 'customer' && channel?.customer?.model === 'Restaurant';

/**
 * Logo URL of a store channel ('' when the store has none or the channel is no store channel).
 * @param {Object} channel
 * @returns {string}
 */
export const storeChannelLogo = (channel) => {
    if (!isStoreChannel(channel)) {
        return '';
    }
    const logo = channel?.store?.logo;

    return typeof logo === 'string' && /^(https?:\/\/|data:image\/)/i.test(logo.trim()) ? logo.trim() : '';
};

/**
 * Closed channel — members only (private channel, personal module-app feed, mail-thread discussion).
 * Rendered with a small lock badge bottom right of the channel icon.
 * @param {Object} channel
 * @returns {boolean}
 */
export const isClosedChannel = (channel) => channel?.type === 'private'
    || (!!channel?.moduleApp && typeof channel.moduleApp === 'object' && !!channel.moduleApp.appKey)
    || isMailThreadChannel(channel);

/**
 * MaterialCommunityIcons glyph for a channel type.
 * @param {Object} channel
 * @returns {string}
 */
export const channelIcon = (channel) => {
    // A glyph picked for a public/private channel wins over the type default.
    if ((channel?.type === 'public' || channel?.type === 'private') && CHANNEL_ICON_CHOICES.includes(channel.icon)) {
        return channel.icon;
    }
    switch (channel?.type) {
        case 'direct':
            return 'account-outline';
        case 'group':
            return 'account-multiple-outline';
        case 'record':
            return isMailThreadChannel(channel) ? 'email-outline' : 'file-document-outline';
        case 'customer':
            return 'account-tie-outline';
        default:
            return 'pound';
    }
};

/**
 * Section bucket of a channel on the chat home.
 * @param {Object} channel
 * @returns {'starred'|'channels'|'direct'|'records'|'customers'}
 */
export const channelSection = (channel) => {
    if (channel?.me?.starred) {
        return 'starred';
    }
    switch (channel?.type) {
        case 'direct':
        case 'group':
            return 'direct';
        case 'record':
            return isMailThreadChannel(channel) ? 'mail' : 'records';
        case 'customer':
            return 'customers';
        default:
            return 'channels';
    }
};

/**
 * Whether the channel is muted right now (notifyLevel none or mutedUntil in future).
 * @param {Object} channel
 * @param {number} [now=Date.now()]
 * @returns {boolean}
 */
export const isChannelMuted = (channel, now = Date.now()) => {
    const me = channel?.me;
    if (!me) {
        return false;
    }
    if (me.notifyLevel === 'none') {
        return true;
    }

    return timeOf(me.mutedUntil) > now;
};

/**
 * Unread + mention totals for badges (archived / hidden / muted excluded,
 * mentions always counted).
 * @param {Array} channels
 * @returns {{unreadTotal: number, mentionTotal: number}}
 */
export const computeUnreadTotals = (channels) => arr(channels).reduce((acc, channel) => {
    if (!channel?.me || channel.archived || channel.me.hidden) {
        return acc;
    }
    const mentions = Number(channel.me.mentionCount) || 0;
    const unread = isChannelMuted(channel) ? mentions : (Number(channel.me.unreadCount) || 0);
    acc.unreadTotal += unread;
    acc.mentionTotal += mentions;

    return acc;
}, {unreadTotal: 0, mentionTotal: 0});

/**
 * Whether a message mentions the viewer (directly or via @channel).
 * @param {Object} message
 * @param {string} meId
 * @returns {boolean}
 */
export const mentionsUser = (message, meId) => {
    const mentions = message?.mentions;
    if (!mentions || !meId) {
        return false;
    }
    if (mentions.channel) {
        return true;
    }

    return arr(mentions.users).some((user) => idOf(user) === String(meId));
};

/**
 * Detect the mentions present in a composed text.
 * @param {string} text Final text.
 * @param {Object<string, string>} picked Map of inserted display name → user id.
 * @returns {{users: string[], channel: boolean}}
 */
export const extractMentions = (text, picked = {}) => {
    const value = String(text || '');
    const users = Object.entries(picked || {})
        .filter(([name, id]) => id && value.includes(`@${name}`))
        .map(([, id]) => String(id));

    return {users: Array.from(new Set(users)), channel: CHANNEL_MENTION_RE.test(value)};
};

/**
 * The `@query` being typed at the end of the text, or null.
 * @param {string} text
 * @returns {string|null}
 */
export const activeMentionQuery = (text) => {
    const match = /(?:^|\s)@([^\s@]{0,30})$/.exec(String(text || ''));

    return match ? match[1] : null;
};

/**
 * Whether an attachment is an image.
 * @param {Object} attachment
 * @returns {boolean}
 */
export const isImageAttachment = (attachment) => /^image\//i.test(String(attachment?.mimeType || ''))
    || /\.(png|jpe?g|gif|webp|heic)$/i.test(String(attachment?.name || ''));

/**
 * Human-readable byte size.
 * @param {number} bytes
 * @returns {string}
 */
export const formatBytes = (bytes) => {
    const value = Number(bytes) || 0;
    if (value <= 0) {
        return '';
    }
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(0)} KB`;
    }

    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Local calendar-day key of a date (YYYY-M-D).
 * @param {*} value
 * @returns {string}
 */
export const dayKeyOf = (value) => {
    const ms = timeOf(value);
    if (!ms) {
        return '';
    }
    const date = new Date(ms);

    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

/**
 * Build the INVERTED FlatList rows (newest first) with day separators, the
 * unread marker and sender grouping.
 *
 * @param {Array} messages oldest → newest
 * @param {{unreadAfterId?: (string|null), unreadFromIndex?: (number|null)}} [options]
 *   `unreadAfterId` = last read message id; marker goes after it. When that id
 *   is not loaded, `unreadFromIndex` (index in `messages`) is used instead.
 * @returns {Array<{type: 'message'|'day'|'unread', key: string, message?: Object, grouped?: boolean, day?: number}>}
 */
export const buildChatRows = (messages, {unreadAfterId = null, unreadFromIndex = null} = {}) => {
    const list = arr(messages);
    let markerIndex = -1;
    if (unreadAfterId) {
        const readIndex = list.findIndex((m) => m._id === unreadAfterId);
        if (readIndex >= 0 && readIndex < list.length - 1) {
            markerIndex = readIndex + 1;
        }
    }
    if (markerIndex < 0 && Number.isInteger(unreadFromIndex) && unreadFromIndex > 0 && unreadFromIndex < list.length) {
        markerIndex = unreadFromIndex;
    }

    const rows = [];
    let previous = null;
    list.forEach((message, index) => {
        const day = dayKeyOf(message.createdAt) || 'pending';
        if (!previous || (dayKeyOf(previous.createdAt) || 'pending') !== day) {
            rows.push({type: 'day', key: `day-${day}-${index}`, day: timeOf(message.createdAt) || Date.now()});
            previous = null;
        }
        if (index === markerIndex) {
            rows.push({type: 'unread', key: 'unread-marker'});
            previous = null;
        }
        const sameSender = previous
            && previous.kind !== 'system'
            && message.kind !== 'system'
            && idOf(previous.sender) && idOf(previous.sender) === idOf(message.sender)
            && Math.abs(timeOf(message.createdAt) - timeOf(previous.createdAt)) < GROUP_WINDOW_MS;
        rows.push({
            type: 'message',
            key: message._id || message.clientMessageId || `idx-${index}`,
            message,
            grouped: !!sameSender,
        });
        previous = message;
    });

    return rows.reverse();
};

/**
 * Short preview line of a channel's last message.
 * @param {Object} channel
 * @returns {string}
 */
export const previewText = (channel) => {
    const preview = channel?.lastMessagePreview;
    if (!preview) {
        return textOf(channel?.topic) || textOf(channel?.description);
    }
    const sender = textOf(preview.senderName);
    const text = textOf(preview.text).replace(/\s+/g, ' ').trim();

    return sender ? `${sender}: ${text}` : text;
};

/**
 * Compact time label: HH:MM today, else a short date.
 * @param {*} value
 * @param {string} [locale]
 * @returns {string}
 */
export const shortTimeLabel = (value, locale) => {
    const ms = timeOf(value);
    if (!ms) {
        return '';
    }
    const date = new Date(ms);
    const now = new Date();
    try {
        if (dayKeyOf(date) === dayKeyOf(now)) {
            return date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
        }

        return date.toLocaleDateString(locale, {month: 'short', day: 'numeric'});
    } catch (_) {
        return date.toISOString().slice(0, 10);
    }
};

/**
 * Key of a record ref (`kind:id`).
 * @param {{kind: string, id: *}} ref
 * @returns {string}
 */
export const recordKey = (ref) => `${String(ref?.kind || '')}:${idOf(ref?.id)}`;

/**
 * Text emoticons rendered as emoji. Only matched as a standalone token (start
 * of text or after whitespace, followed by end, whitespace or punctuation), so
 * `10:30`, `http://`, `a:b` and `(see 1:D)` are never touched.
 */
export const EMOTICON_EMOJI = Object.freeze({
    ':-)': '🙂',
    ':)': '🙂',
    ':-(': '🙁',
    ':(': '🙁',
    ':-D': '😄',
    ':D': '😄',
    'xD': '😆',
    'XD': '😆',
    ';-)': '😉',
    ';)': '😉',
    ':-P': '😛',
    ':P': '😛',
    ':-p': '😛',
    ':p': '😛',
    ':-O': '😮',
    ':O': '😮',
    ':o': '😮',
    ':-*': '😘',
    ':*': '😘',
    ":'(": '😢',
    ':-/': '😕',
    ':/': '😕',
    ':-|': '😐',
    ':|': '😐',
    '<3': '❤️',
    '</3': '💔',
});

/** Slack-style `:shortcode:` emoji. Unknown codes stay as typed. */
export const SHORTCODE_EMOJI = Object.freeze({
    smile: '😄',
    slightly_smiling_face: '🙂',
    grin: '😁',
    joy: '😂',
    rofl: '🤣',
    laughing: '😆',
    sweat_smile: '😅',
    blush: '😊',
    wink: '😉',
    heart_eyes: '😍',
    sunglasses: '😎',
    thinking: '🤔',
    neutral_face: '😐',
    cry: '😢',
    sob: '😭',
    angry: '😠',
    scream: '😱',
    facepalm: '🤦',
    shrug: '🤷',
    thumbsup: '👍',
    '+1': '👍',
    thumbsdown: '👎',
    '-1': '👎',
    ok_hand: '👌',
    clap: '👏',
    pray: '🙏',
    muscle: '💪',
    wave: '👋',
    eyes: '👀',
    heart: '❤️',
    fire: '🔥',
    tada: '🎉',
    rocket: '🚀',
    star: '⭐',
    '100': '💯',
    white_check_mark: '✅',
    check: '✅',
    x: '❌',
    warning: '⚠️',
    coffee: '☕',
    beer: '🍺',
});

const EMOTICON_PATTERN = new RegExp(
    `(^|\\s)(${Object.keys(EMOTICON_EMOJI)
        .sort((a, b) => b.length - a.length)
        .map((code) => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|')})(?=$|\\s|[.,!?;:)])`,
    'g'
);
const SHORTCODE_PATTERN = /:([a-z0-9_+-]{1,32}):/gi;

/**
 * Replace text emoticons (`:D`, `<3`) and `:shortcode:`s with emoji.
 * @param {string} text
 * @returns {string}
 */
export const emoticonsToEmoji = (text = '') => {
    const body = String(text || '');

    if (!body || (body.indexOf(':') === -1 && body.indexOf(';') === -1 && body.indexOf('<') === -1 && !/x/i.test(body))) {
        return body;
    }

    return body
        .replace(SHORTCODE_PATTERN, (whole, code) => SHORTCODE_EMOJI[code.toLowerCase()] || whole)
        .replace(EMOTICON_PATTERN, (whole, lead, code) => `${lead}${EMOTICON_EMOJI[code] || code}`);
};

/**
 * Split message text into renderable segments: plain text, links and mentions.
 * @param {string} text
 * @param {string[]} [mentionNames] Names to highlight after `@`.
 * @returns {Array<{type: 'text'|'link'|'mention', value: string}>}
 */
export const tokenizeMessageText = (text = '', mentionNames = []) => {
    const body = String(text || '');

    if (!body) {
        return [];
    }
    const names = (Array.isArray(mentionNames) ? mentionNames : [])
        .filter((name) => typeof name === 'string' && name.trim())
        .sort((a, b) => b.length - a.length)
        .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const mentionPattern = names.length
        ? `@(?:${names.join('|')}|channel|here)`
        : '@(?:channel|here)';
    const pattern = new RegExp(`(https?:\\/\\/[^\\s<]+)|(${mentionPattern})`, 'gi');
    const segments = [];
    let lastIndex = 0;
    let match = pattern.exec(body);

    while (match) {
        if (match.index > lastIndex) {
            segments.push({
                type: 'text',
                value: emoticonsToEmoji(body.slice(lastIndex, match.index)),
            });
        }
        segments.push({
            type: match[1] ? 'link' : 'mention',
            value: match[0],
        });
        lastIndex = match.index + match[0].length;
        match = pattern.exec(body);
    }
    if (lastIndex < body.length) {
        segments.push({
            type: 'text',
            value: emoticonsToEmoji(body.slice(lastIndex)),
        });
    }

    return segments;
};
